/* @flow */
/* eslint-disable react/no-did-mount-set-state */
import qs from 'qs';
import * as React from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import Group from 'entities/Group';
import { consume } from 'lib/queryParams';
import CurrentUser from 'auth/CurrentUser';
import { browserHistory } from 'react-router';
import GroupSettingsModal from 'groups/components/GroupSettingsModal';
import DraftedComposer from 'chat/components/DraftedComposer';
import { SplitComposerModal } from 'chat/components/SplitComposer';
import GroupSubscribedSettingModal from 'groups/components/GroupSubscribedSettingModal';
import SupportDialog from 'support/components/SupportDialog';
import UsersInviteModal from 'users/components/UsersInviteModal';
import {
  setShowDraftAnnouncement,
  saveDraftAnnouncement,
} from 'groups/actions/DraftMessageActions';
import type { DraftAnnouncement } from 'groups/actions/DraftMessageActions';

import './styles/GroupView.scss';

const MODALS = {
  USERS_INVITE_MODAL: Symbol('users_invite_modal'),
  GROUP_SETTING_MODAL: Symbol('group_setting_modal'),
  MESSAGE_CREATE_MODAL: Symbol('message_create_modal'),
  TARGET_BY_STUDENT_ID_MODAL: Symbol('target_by_student_id_modal'),
  HELP_MODAL: Symbol('help_modal'),
};

const ACTIONS = {
  NEW_MESSAGE: 'new-message',
  NEW_TARGET_BY_STUDENT_ID_MESSAGE: 'tbsi-message',
};
const QUERY_PARAMETERS = ['action', 'settings', 'actionData', 'invite', 'queryParams', 'help'];

function messageFromQuery(query): DraftAnnouncement {
  if (!query || Object.keys(query).length <= 0) {
    return { __typename: 'DraftAnnouncement', body: null, attachmentId: null, targets: [] };
  }

  const { body, attachmentId, groupId: groupStrId, role, link } = query;

  const groupId = parseInt(groupStrId, 10);
  const targets = Number.isNaN(groupId)
    ? []
    : [{ __typename: 'DraftAnnouncementTarget', group_id: groupId, role: role || 'everyone' }];
  const modbody = body && link ? `${body} ${link}` : body || link || undefined;

  return {
    __typename: 'DraftAnnouncement',
    body: modbody,
    attachmentId: attachmentId || null,
    targets,
  };
}

function setAnnouncementDraftForQuery(params: Object) {
  if (params.action === ACTIONS.NEW_MESSAGE) {
    const message = messageFromQuery(qs.parse(params.actionData || params.queryParams));
    saveDraftAnnouncement(message);
    setShowDraftAnnouncement(true);
  }
}

function getModalForQuery(group: ?Object, params: Object): ?Symbol {
  if (params.action === ACTIONS.NEW_MESSAGE) {
    return MODALS.MESSAGE_CREATE_MODAL;
  }

  if (params.action === ACTIONS.NEW_TARGET_BY_STUDENT_ID_MESSAGE) {
    return MODALS.TARGET_BY_STUDENT_ID_MODAL;
  }

  if (group && !group.isRoot() && params.settings) {
    return MODALS.GROUP_SETTING_MODAL;
  }

  if (group && !group.isRoot() && params.invite) {
    return MODALS.USERS_INVITE_MODAL;
  }

  if (params.help) {
    return MODALS.HELP_MODAL;
  }

  return null;
}

type Props = {
  group?: Group,
};

type State = {
  modal: ?Symbol,
};

/* This components consumes query parameters related to classes and takes appropriate actions.
 *
 *   https://www.remind.com/classes?invite=true
 *   https://www.remind.com/classes?settings=true
 *   https://www.remind.com/classes?new-message=true
 *   https://www.remind.com/classes?help=true
 *
 */
export class GroupViewQueryParametersConsumer extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State = {
    modal: null,
  };

  componentDidMount() {
    const params = consume(browserHistory, QUERY_PARAMETERS);

    if (Object.keys(params).length <= 0) {
      return;
    }

    setAnnouncementDraftForQuery(params);
    const modal = getModalForQuery(this.props.group, params);

    if (modal) {
      this.setState({ modal });
    }
  }

  clearModalFromState = () => {
    this.setState({ modal: null });
  };

  getModal() {
    const { modal } = this.state;
    const { group } = this.props;

    if (modal === MODALS.MESSAGE_CREATE_MODAL) {
      return <DraftedComposer onRequestHide={this.clearModalFromState} />;
    }

    if (modal === MODALS.TARGET_BY_STUDENT_ID_MODAL) {
      return (
        <SplitComposerModal
          defaultTab="target_by_student_id"
          onRequestHide={this.clearModalFromState}
        />
      );
    }

    if (modal === MODALS.HELP_MODAL) {
      return <SupportDialog onRequestHide={this.clearModalFromState} />;
    }

    if (group && modal === MODALS.USERS_INVITE_MODAL) {
      return <UsersInviteModal group={group} onRequestHide={this.clearModalFromState} />;
    }

    if (group && modal === MODALS.GROUP_SETTING_MODAL && group.canEdit()) {
      return (
        <GroupSettingsModal
          group={group}
          onRequestHide={this.clearModalFromState}
          userIsTeacher={CurrentUser.getUser().isTeacher()}
          onCodeChange={code => this.context.router.push(`/classes/${code}`)}
        />
      );
    }

    if (group && modal === MODALS.GROUP_SETTING_MODAL) {
      return <GroupSubscribedSettingModal group={group} onRequestHide={this.clearModalFromState} />;
    }

    return null;
  }

  render() {
    const modal = this.getModal();

    if (!modal) {
      return null;
    }

    return <Modal isOpen>{modal}</Modal>;
  }
}

export default GroupViewQueryParametersConsumer;
